import React from "react";
import EditableFormattedMessage from "./EditableFormattedMessage";
import ppeFullLogo from '../ressources/logo-with-text-green.svg';

const getDurationLocaleConfig = (locale) => {
  const normalized = String(locale || 'en').toLowerCase();

  if (normalized.startsWith('ja')) {
    return { h: '時間', m: '分', s: '秒', compact: true };
  }

  if (normalized.startsWith('zh-tw') || normalized.startsWith('zh-hk') || normalized.includes('hant')) {
    return { h: '小時', m: '分', s: '秒', compact: true };
  }

  if (normalized.startsWith('zh')) {
    return { h: '小时', m: '分', s: '秒', compact: true };
  }

  return { h: 'h', m: 'm', s: 's', compact: false };
};

const formatDuration = (duration, locale) => {
  const unitCfg = getDurationLocaleConfig(locale);

  const formatParts = (hours, minutes, seconds) => {
    if (unitCfg.compact) {
      return `${hours}${unitCfg.h}${minutes}${unitCfg.m}${seconds}${unitCfg.s}`;
    }
    return `${hours}${unitCfg.h} ${minutes}${unitCfg.m} ${seconds}${unitCfg.s}`;
  };

  const fallback = formatParts(0, 33, 33);

  const buildDuration = (hours, minutes, seconds) => {
    const h = Number(hours);
    const m = Number(minutes);
    const s = Number(seconds);

    if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) {
      return fallback;
    }

    if (h < 0 || m < 0 || s < 0) {
      return fallback;
    }

    return formatParts(Math.floor(h), Math.floor(m), Math.floor(s));
  };

  try {
    if (duration === null || typeof duration === 'undefined' || duration === '' || duration === 'null') {
      return fallback;
    }

    if (typeof duration === 'object' && duration !== null && 'hours' in duration) {
      return buildDuration(duration.hours, duration.minutes, duration.seconds);
    }
    
    if (typeof duration === 'string') {
        const compactMatch = duration.match(/^(\d+)h\s*(\d+)m\s*(\d+)s$/i);
        if (compactMatch) {
          return buildDuration(compactMatch[1], compactMatch[2], compactMatch[3]);
        }

        const genericThreeNumbers = duration.match(/(\d+)\D+(\d+)\D+(\d+)/);
        if (genericThreeNumbers) {
          return buildDuration(genericThreeNumbers[1], genericThreeNumbers[2], genericThreeNumbers[3]);
        }

        const jsonString = duration
          .replace(/(\w+):/g, '"$1":')  // Add quotes around property names
          .replace(/'/g, '"');          // Replace single quotes with double quotes
        
        try {
          const parsed = JSON.parse(jsonString);
          return buildDuration(parsed.hours, parsed.minutes, parsed.seconds);
        } catch (e2) {
          //console.log("Failed to convert to JSON");
        }
    }
    
    return fallback;
  } catch (e) {
    //console.error("ERROR in formatDuration:", e);
    return fallback;
  }
};

const formatDateForDisplay = (value, locale, options) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(locale, options);
};

const getComparisonGroupValue = (...sources) => {
  const isPosteCode = (value) => /^POSTE[1-5]$/i.test(String(value || '').trim());

  const extractFromNode = (node) => {
    if (!node) return '';

    if (Array.isArray(node)) {
      for (const item of node) {
        const found = extractFromNode(item);
        if (found) return found;
      }
      return '';
    }

    if (typeof node !== 'object') return '';

    for (const [key, value] of Object.entries(node)) {
      if (/^poste$/i.test(key) || /^POSTE_/i.test(key)) {
        if (typeof value === 'string' && value.trim()) return value;
      }
      if (isPosteCode(value)) return String(value);
    }

    const nestedCandidates = [
      node.state,
      node.answers,
      node.results,
      node.dataForGraphs,
      node.data,
      node.user,
      node.location && node.location.state
    ];

    for (const nested of nestedCandidates) {
      const found = extractFromNode(nested);
      if (found) return found;
    }

    return '';
  };

  let raw = '';
  for (const source of sources) {
    const found = extractFromNode(source);
    if (found) {
      raw = found;
      break;
    }
  }

  if (!raw) return '';

  const mapping = {
    POSTE1: 'INFOPERSO5A',
    POSTE2: 'INFOPERSO5B',
    POSTE3: 'INFOPERSO5C',
    POSTE4: 'INFOPERSO5D',
    POSTE5: 'INFOPERSO5E'
  };

  const normalized = String(raw).toUpperCase().trim();
  return mapping[normalized] || String(raw);
};

function EditReportCoverPage({ messagesData, locale, messages, updateMessage, code, datePassed, timeTaken, dataForGraphs, location }) {
  const comparisonGroup = getComparisonGroupValue(dataForGraphs, location && location.state, location);
  const isComparisonGroupMessageKey = /^INFOPERSO5[A-E]$/.test(comparisonGroup);

  return (
    <div className="PageCharts-chart">
      <div className="report-alt-page">
        <img
          alt="ppe-logo"
          className="report-main-logo"
          src={ppeFullLogo}
          style={{ position: "absolute", left: "40px", top: "40px", width: "220px" }}
        />
        <div className="report-alt-page-candidate">
          <div className="candidate-separator report-title">
            <b>
              <EditableFormattedMessage keys={messagesData} locale={locale} msgKey="REPORT_TITLE" messages={messages} updateMessage={updateMessage}/>
            </b>
          </div>
          <div className="candidate-separator">
            <b>
              <EditableFormattedMessage keys={messagesData} locale={locale} msgKey="CANDIDATE" messages={messages} updateMessage={updateMessage}/>
            </b>{" "}
            {code}
          </div>
          <div className="candidate-separator">
            <b>
              <EditableFormattedMessage keys={messagesData} locale={locale} msgKey="COMPARISONGROUP" messages={messages} updateMessage={updateMessage}/>
            </b>{" "}
            {isComparisonGroupMessageKey ? (
              <EditableFormattedMessage
                keys={messagesData}
                locale={locale}
                msgKey={comparisonGroup}
                messages={messages}
                updateMessage={updateMessage}
              />
            ) : (
              comparisonGroup
            )}
          </div>
          <div className="candidate-separator">
            <b>
              <EditableFormattedMessage keys={messagesData} locale={locale} msgKey="TEST_DATE" messages={messages} updateMessage={updateMessage}/>
            </b>{" "}
            {formatDateForDisplay(datePassed, undefined, {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
            <div className="candidate-separator">
              <b><EditableFormattedMessage locale={locale} msgKey="TEST_TIME_TAKEN" updateMessage={updateMessage} /></b>{" "}
              {formatDuration(timeTaken, locale)}
            </div>
        </div>
      </div>
      <div className="pdf-footer"></div>
    </div>
  );
}

export default EditReportCoverPage;